"use client"

import { useEffect, useState, useMemo, useRef } from "react"
import { Carousel } from "./carousel"
import { useSharedData } from "@/app/store/useDestinationSharedData"
import gsap from "@/lib/gsap-init"
import { useTranslations, useLocale } from 'next-intl'
import { useLocalizedField } from '@/hooks/useLocalizedField'

export function DestinationReels() {
  const t = useTranslations()
  const locale = useLocale()
  const { getLocalizedField } = useLocalizedField()

  const {
    destinations,
    loadingDestinations,
    errorDestinations,
    fetchDestinations,
  } = useSharedData()
  const sectionRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  // Load API destinations from Zustand
  useEffect(() => {
    if(destinations.length==0){
    fetchDestinations()

    }
  }, [fetchDestinations])

    useEffect(() => {
    // Wait for refs to be available and destinations to be loaded
    if (!sectionRef.current || !carouselRef.current) return
    if (loadingDestinations) return // Don't animate until content is loaded

    const ctx = gsap.context(() => {
      // Set initial state
      gsap.set(carouselRef.current, { opacity: 0, y: 60 })

      // Carousel animation
      gsap.to(carouselRef.current, {
        opacity: 1,
        y: 0,
        duration: 2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "top 50%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [loadingDestinations, destinations])

  // 🔥 Transform API data to match Carousel props — INCLUDING videoUrl
  const carouselItems = useMemo(() => {
    if (!destinations || destinations.length === 0) return []

    return destinations.map((d:any) => ({
      id: d.slug || d.name,
      name: getLocalizedField(d, 'name'),
      title: getLocalizedField(d, 'short_title') || getLocalizedField(d, 'name'),
      description: getLocalizedField(d, 'short_description'),
      image: d.gallery[1],
      videoUrl: d.video_link || "",          // ⭐ your video link here
    }))
  }, [destinations, getLocalizedField])

  // Pick 6 random items (you can adjust)
  const randomReels = useMemo(() => {
    if (carouselItems.length === 0) return []
    return [...carouselItems].sort(() => Math.random() - 0.5).slice(0, 6)
  }, [carouselItems])

  return (
    <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background relative" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {loadingDestinations && (
          <p className="text-muted-foreground">{t('common.loading')}</p>
        )}

        {errorDestinations && (
          <p className="text-red-500">{t('common.error')}</p>
        )}

        {!loadingDestinations && randomReels.length > 0 && (
          <div ref={carouselRef}>
            <Carousel 
              items={randomReels}
              showItemsCount={3.2}
              title={`${t('navigation.destinations')} ${t('common.reels')}`}
              paragraph={t('sections.popularDestinations')}
            />
          </div>
        )}
      </div>
    </section>
  )
}
